import { prepareQueryParams } from '@homeviews/utils'
import { api, API_V2_BASE_URL } from '@homeviews/store'
import type { MetaTotal } from '@homeviews/analytics'

interface IWidgetsResponse {
  widgets: any[]
}

interface IWidgetResponse {
  widget: {
    id: number
    title: string
    website: string
    widget_type: string
  }
  code: string
}

type TypeID = number | string

type V2WidgetsQueryParams = {
  // When true, get Widgets only for authenticated user's Developer
  portalDeveloper: boolean
  lastViewDate: string // e.g. '2021-11-03'
}

export const widgetsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getWidgets: builder.query<IWidgetsResponse, any>({
      query: ({ developer, sort }) => {
        return {
          url: `/companies/${developer}/widgets`,
          params: {
            use_athena_widgets: 1,
            ...sort,
          },
        }
      },
      providesTags: <any>['Widgets'],
    }),
    getV2Widgets: builder.query<MetaTotal, V2WidgetsQueryParams>({
      query: (params) => ({
        url: `${API_V2_BASE_URL}/widgets${prepareQueryParams(params)}`,
      }),
    }),
    getWidget: builder.query<IWidgetResponse, any>({
      query: ({ developer, id, layout }) => {
        if (!id) {
          return null
        }
        return {
          url: `/companies/${developer}/widgets/${id}`,
          params: {
            use_athena_widgets: 1,
            layout,
          },
        }
      },
    }),
    getWidgetTypes: builder.query<any, TypeID>({
      query: (developer) => {
        return {
          url: `/companies/${developer}/widgets/list-available-widget-types`,
        }
      },
    }),
    getWidgetDevelopments: builder.query<any, TypeID>({
      query: (developer) => {
        return {
          url: `/companies/${developer}/widgets/list-available-developments`,
        }
      },
    }),
    createWidget: builder.mutation({
      query: ({ developer, data }) => {
        return {
          url: `/companies/${developer}/widgets`,
          method: 'POST',
          body: data,
          params: { use_athena_widgets: 1 },
        }
      },
      invalidatesTags: <any>['Widgets'],
    }),
    generateWidget: builder.mutation({
      query: ({ developer, data, widgetId }) => {
        return {
          url: `/companies/${developer}/widgets/${widgetId}`,
          method: 'PATCH',
          body: data,
          params: { use_athena_widgets: 1 },
        }
      },
      invalidatesTags: <any>['Widgets'],
    }),
    deleteWidget: builder.mutation<IWidgetResponse, any>({
      query: ({ developer, id }) => {
        return {
          url: `/companies/${developer}/widgets/${id}`,
          method: 'DELETE',
          params: { use_athena_widgets: 1 },
        }
      },
      invalidatesTags: <any>['Widgets'],
    }),
  }),
})

export const {
  useGetWidgetDevelopmentsQuery,
  useGetWidgetTypesQuery,
  useGetWidgetQuery,
  useGetWidgetsQuery,
  useGetV2WidgetsQuery,
  useCreateWidgetMutation,
  useGenerateWidgetMutation,
  useDeleteWidgetMutation,
} = widgetsApi
